import { useEffect, useState } from 'react'
import { Button, Group, PasswordInput, Stack, Text, TextInput } from '@mantine/core'
import { ResizableDrawer } from '@/components/ResizableDrawer'
import { Callout } from '@/components/ui'
import {
  createEntraConnection, updateEntraConnection,
  type SsoConnection, type EntraConnectionUpdate,
} from '@/api/sso'
import { ssoErrorMessage } from './ssoErrors'
import { useFormSimples } from './useFormSimples'
import { validaGuid, validaRealm } from './ssoValidacao'

interface Props {
  opened: boolean
  onClose: () => void
  conexao: SsoConnection | null
  realmSugerido?: string
  onSaved: (c: SsoConnection) => void
}

interface Values {
  name: string
  keycloak_realm: string
  entra_tenant_id: string
  client_id: string
  client_secret: string
}

const MONO = { input: { fontFamily: 'var(--mantine-font-family-monospace)' } }

// Nome exibido no login (displayName do realm e do IdP no Keycloak).
const validaNome = (v: string): string | null => {
  const t = v.trim()
  if (!t) return 'Informe o nome exibido.'
  if (t.length > 120) return 'No máximo 120 caracteres.'
  return null
}

export function ConexaoDrawer({ opened, onClose, conexao, realmSugerido, onSaved }: Props) {
  const edicao = conexao !== null
  const inicial = (): Values => ({
    name: conexao?.name ?? '',
    keycloak_realm: conexao?.keycloak_realm ?? realmSugerido ?? '',
    entra_tenant_id: conexao?.config.entra_tenant_id ?? '',
    client_id: conexao?.config.client_id ?? '',
    client_secret: '',
  })
  const form = useFormSimples<Values>(inicial, {
    name: validaNome,
    keycloak_realm: (v) => (edicao ? null : validaRealm(v)),
    entra_tenant_id: validaGuid,
    client_id: validaGuid,
    client_secret: (v) => (edicao || v.length > 0 ? null : 'Informe o client secret do Entra.'),
  }, opened, conexao?.id ?? null)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  // O secret nunca sobrevive ao drawer: o hook zera tudo ao abrir e ao fechar.
  useEffect(() => { if (!opened) setErro(null) }, [opened])

  const fechar = () => {
    form.reset()
    setErro(null)
    onClose()
  }

  const salvar = async (v: Values) => {
    setErro(null)
    setSalvando(true)
    try {
      let saved: SsoConnection
      if (edicao) {
        const body: EntraConnectionUpdate = { entra_tenant_id: v.entra_tenant_id, client_id: v.client_id }
        if (v.client_secret) body.client_secret = v.client_secret
        // Só manda o nome se mudou: renomear reprovisiona o displayName no Keycloak.
        if (v.name.trim() !== conexao.name) body.name = v.name.trim()
        saved = await updateEntraConnection(conexao.id, body)
      } else {
        saved = await createEntraConnection({ ...v, name: v.name.trim() })
      }
      form.limpa('client_secret')
      onSaved(saved)
      onClose()
    } catch (e) {
      const { message, field } = ssoErrorMessage(e)
      if (field && field in v && (!edicao || field !== 'keycloak_realm')) form.setErroServidor(field as keyof Values, message)
      else setErro(message)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <ResizableDrawer
      opened={opened}
      onClose={fechar}
      title={edicao ? 'Provedor Entra' : 'Novo provedor Entra'}
      storageKey="sso-conexao-drawer"
      defaultWidth={560}
    >
      <form onSubmit={(e) => { e.preventDefault(); void salvar(form.values) }}>
        <Stack gap="md">
          {erro && <Callout variant="danger">{erro}</Callout>}
          <TextInput
            label="Nome exibido"
            description="O que as pessoas veem na tela de login. O realm e o alias técnicos não mudam."
            placeholder="ex.: AD - Amazonas Inovare"
            {...form.props('name')}
          />
          {edicao ? (
            <div>
              <Text size="sm" fw={500}>Realm</Text>
              <Text ff="monospace">{conexao.keycloak_realm}</Text>
            </div>
          ) : (
            <TextInput label="Realm" styles={MONO} {...form.props('keycloak_realm')} />
          )}
          <TextInput label="Tenant Entra (GUID)" styles={MONO} {...form.props('entra_tenant_id')} />
          <TextInput label="Client ID (GUID)" styles={MONO} {...form.props('client_id')} />
          <PasswordInput
            label="Client secret"
            autoComplete="new-password"
            description={edicao ? 'Deixe em branco para manter o atual' : undefined}
            {...form.props('client_secret')}
          />
          <Group justify="flex-start">
            <Button type="submit" loading={salvando} disabled={!form.valido}>Salvar</Button>
          </Group>
        </Stack>
      </form>
    </ResizableDrawer>
  )
}
